/**
 * 授權 token 的統一存取入口。
 *
 * 進入應用時，token 會由路由(url)上帶進來，需要保存到 sessionStorage，
 * 存的 key 固定為 jb-authorization（與後端約定、請求 header 名稱保持一致）。
 */

import { getUrlParamFromUrl } from './url-param';

/** sessionStorage 中存放 token 的 key */
export const TOKEN_KEY = 'jb-authorization';

/** url 上可能承載 token 的引數名，按優先順序匹配 */
const URL_TOKEN_KEYS = [
  'token',
  'jb-authorization',
  'accessToken',
  'access_token',
  'authorization',
];

function getStorage() {
  try {
    // 隱私模式或非瀏覽器環境下訪問 sessionStorage 可能直接拋錯
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

/**
 * 讀取當前 token
 * @returns {String|null} 未登入/未設定時返回 null
 */
export function getToken() {
  const storage = getStorage();
  return storage ? storage.getItem(TOKEN_KEY) : null;
}

/**
 * 寫入 token
 * @param {String} token
 * @returns {String|null} 實際寫入的 token
 */
export function setToken(token) {
  if (!token) {
    return null;
  }
  const storage = getStorage();
  if (!storage) {
    return null;
  }
  storage.setItem(TOKEN_KEY, token);
  return token;
}

/** 清除 token */
export function removeToken() {
  const storage = getStorage();
  if (storage) {
    storage.removeItem(TOKEN_KEY);
  }
}

/**
 * 從 url 上取出 token
 * @param {String} [url] 目標 url，預設取當前位址欄
 * @returns {String} 取不到時返回空字串
 */
export function getTokenFromUrl(url) {
  return getUrlParamFromUrl(url, URL_TOKEN_KEYS);
}

/**
 * 進入應用 / 頁面時呼叫：路由(url)上帶有 token 才寫入 sessionStorage。
 * 沒有 token 時不執行任何操作（不寫入、不讀取、不清除），直接返回 null。
 * @param {String} [url] 目標 url，預設取當前位址欄
 * @returns {String|null} 有 token 時返回寫入的 token，沒有時返回 null
 */
export function saveTokenFromUrl(url) {
  const urlToken = getTokenFromUrl(url);
  if (!urlToken) {
    return null;
  }
  return setToken(urlToken);
}
